import React from 'react';
import { Modal, Select } from 'antd';
import styled, { CSSProperties, FlattenSimpleInterpolation } from 'styled-components';

export type Option = {
    value: string;
    text: string;
    color?: string;
};

export type GroupedOptions = {
    [name: string]: Option[];
};

export const enumToDropdownOptions = (object: any): Array<Option> => {
    return Object.keys(object).map((key) => {
        return { value: key, text: object[key] as string };
    });
};
export type ExtendedProps = BaseProps & {
    options: Array<Option> | GroupedOptions;
};
export type BaseProps = {
    onChange?: any;
    onBlur?: any;
    placeHolder?: string;
    style?: CSSProperties;
    selectorStyle?: FlattenSimpleInterpolation;
    value?: string | string[];
    disabled?: boolean;
    showSearch?: boolean;
    optionRenderer?: (option: Option & { color?: string }) => Promise<JSX.Element> | JSX.Element;
    hasError?: boolean;
    width?: string;
    minWidth?: string;
    height?: string;
    preventPopoverBug?: boolean;
    allowClear?: boolean;
    mode?: 'multiple' | 'tags';
    verifyChange?: {
        title?: (changeValue: any) => string;
    };
};

const DropDown = ({
    value,
    options,
    placeHolder,
    style,
    selectorStyle,
    disabled,
    optionRenderer,
    onChange,
    onBlur,
    showSearch,
    hasError,
    width,
    height,
    preventPopoverBug,
    allowClear,
    mode,
    verifyChange
}: ExtendedProps) => {
    const { Option, OptGroup } = Select;

    const renderOptions = (options: Option[]) =>
        options.map((option, index) => (
            <Option key={`${option.value}-${index}`} value={option.value} text={option.text}>
                {optionRenderer ? optionRenderer(option) : option.text}
            </Option>
        ));

    return (
        <Container hasError={hasError} width={width} height={height} selectorStyle={selectorStyle}>
            <Select
                mode={mode}
                showSearch={showSearch}
                optionFilterProp="children"
                filterOption={(input, option) => {
                    const value = String(option?.value).toLowerCase();
                    const children = option?.children ? String(option?.children).toLowerCase() : value;
                    const text = option?.text ? String(option?.text).toLowerCase() : children;
                    return value.indexOf(input.toLowerCase()) >= 0 || text.indexOf(input?.toLowerCase()) >= 0 || children.indexOf(input?.toLowerCase()) >= 0;
                }}
                getPopupContainer={(trigger: any) => document.body}
                dropdownStyle={{ zIndex: 10000 }}
                disabled={disabled}
                style={{ width: 'inherit', height, ...style }}
                placeholder={placeHolder}
                value={value}
                onChange={(value) => {
                    if (verifyChange) {
                        Modal.confirm({
                            title: verifyChange.title ? verifyChange.title(value) : 'Are you sure?',
                            zIndex: 10001,
                            async onOk() {
                                await onChange(value);
                            },
                            onCancel() {}
                        });
                    } else {
                        onChange(value);
                    }
                }}
                onBlur={onBlur}
                dropdownAlign={{ offset: [0, 0] }}
                allowClear={allowClear}
            >
                {Array.isArray(options)
                    ? renderOptions(options)
                    : Object.entries(options).map(([groupName, groupOptions]: [string, Option[]], i: number) => (
                          <OptGroup label={groupName} key={`${groupName}-${i}`}>
                              {renderOptions(groupOptions)}
                          </OptGroup>
                      ))}
            </Select>
        </Container>
    );
};
DropDown.defaultProps = {
    width: '100%',
    height: 'auto',
    style: ''
};
interface ContainerProps {
    hasError?: boolean;
    width?: string;
    height?: string;
    selectorStyle?: FlattenSimpleInterpolation;
}
const Container = styled.div<ContainerProps>`
    position: relative;
    width: ${(props) => props.width};
    & .ant-select-arrow {
        color: ${(props) => props.theme.colors.primaryBlue};
    }

    & .ant-select-selector {
        border-radius: 2vh !important;
        border-color: ${(props) => (props.hasError ? 'red' : props.theme.colors.primaryBlue)} !important;
        ${(props) => props.selectorStyle}
    }
    & .ant-select-focused.ant-select-multiple .ant-select-selector {
        border-radius: 2vh !important;
        box-shadow: ${(props) => (props.hasError ? '0 0 0 2px rgb(255 24 24 / 20%)' : '0 0 0 2px rgba(24, 144, 255, 0.2)')} !important;
    }
    & .ant-select-dropdown {
        z-index: 10000 !important;
    }
    .ant-select-dropdown.ant-select-dropdown-placement-bottomLeft,
    .ant-select-dropdown.ant-select-dropdown-placement-bottomRight,
    .ant-select-dropdown.ant-select-dropdown-placement-topLeft,
    .ant-select-dropdown.ant-select-dropdown-placement-topRight {
        z-index: 10000 !important;
    }
`;

export default DropDown;
